import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ShieldCheck, Fingerprint } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/auth-context';
import { apiFetch } from '@/lib/api';
import SEO from '@/components/SEO';

const VerifyOtp: React.FC = () => {
  const { refreshUser } = useAuth();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const email = searchParams.get('email') || '';
  const defaultRedirect = '/';
  const redirect = searchParams.get('redirect') || defaultRedirect;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiFetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp })
      });

      const data = await response.json();

      if (response.ok) {
        // The backend already sets the cookie, so we must refresh the user context
        await refreshUser();
        navigate(redirect, { replace: true });
      } else {
        setError(data.error || 'Invalid code');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-20 relative overflow-hidden transition-all duration-1000">
      <SEO title="Verify OTP" noindex />
      <div className="absolute inset-x-0 top-0 h-[400px] bg-[#f8fbff] -z-10" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(6,182,212,0.06)_0%,transparent_50%)]" />
      </div>

      <div className="w-full max-w-[400px] relative z-10">
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-cyan-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-cyan-600/20">
            <ShieldCheck className="text-white w-10 h-10" />
          </div>
          <h1 className="text-3xl md:text-4xl font-black font-display text-cyan-950 uppercase tracking-tighter mb-2 leading-none">Security Check</h1>
          <p className="text-zinc-500 font-medium px-4 leading-relaxed text-sm">A verification code was sent to <br /><span className="text-cyan-600 font-bold">{email}</span></p>
        </div>

        <div className="bg-white p-8 rounded-[3rem] border border-cyan-100 shadow-2xl shadow-cyan-900/5">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-3 text-center">
              <label className="text-[10px] font-black uppercase tracking-[0.4em] text-cyan-600/50 block">
                Verification Code
              </label>
              <input
                type="text"
                required
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                className="w-full h-20 bg-cyan-50/30 border border-cyan-100 rounded-2xl text-cyan-950 text-center text-4xl tracking-[0.3em] font-black focus:border-cyan-500 focus:ring-8 focus:ring-cyan-500/5 outline-none transition-all"
                placeholder="000000"
              />
            </div>

            {error && (
              <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-[10px] font-black uppercase tracking-widest text-center animate-in fade-in slide-in-from-top-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full h-16 bg-zinc-950 text-white hover:bg-zinc-900 font-black uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-zinc-900/10 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? 'Verifying...' : (
                <>
                  Verify Account <Fingerprint className="w-5 h-5 ml-1" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-100 text-center">
            <p className="text-zinc-500 text-[9px] font-black uppercase tracking-widest leading-loose">
              Didn't receive the code?{' '}
              <button className="text-cyan-600 hover:underline">
                Resend email
              </button>
            </p>
          </div>
        </div>
        
        <div className="mt-10 text-center">
           <Link to="/login" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-400 hover:text-cyan-600 transition-colors">
              Return to Login
           </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
